import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Link,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  KeyboardArrowRight,
} from "@mui/icons-material";
import CustomText from "../components/CustomText";
import { ScreenContext } from "../context/ScreenContext";
import { useDoctorsDirectory } from "../utils/useDoctorsDirectory";

const CarouselDoctors = ({
  cards = [],
  colorIcon,
  directorio,
  loadingDoctors: loadingDoctorsProp,
}) => {
  const { isMobile } = useContext(ScreenContext);
  const { doctors: sharedDoctors, loading: sharedLoading } =
    useDoctorsDirectory();
  /**
   * Function to navigate between routes.
   * @type {Function}
   */
  const navigate = useNavigate();
  /**
   * Función que maneja la navegación a la página de detalle del doctor.
   * Envía solo un identificador para no exponer la información completa en la URL.
   * @function
   * @returns {void} No retorna ningún valor, solo realiza la navegación.
   */
  const handleDoctorDetails = (doctor) => {
    if (!doctor) return;

    const doctorId = doctor.uid ?? doctor.id ?? doctor.cedula;

    if (!doctorId) return;

    navigate(`/doctorsDetails?doctor=${encodeURIComponent(doctorId)}`, {
      state: {
        doctor,
      },
    });
  };

  const itemsPerPage = isMobile ? 1 : 3;
  const [startIndex, setStartIndex] = useState(0);
  const resolvedCards = cards?.length > 0 ? cards : sharedDoctors;
  const loadingDoctors = Boolean(loadingDoctorsProp ?? sharedLoading);

  const total = resolvedCards?.length ?? 0;

  const handleNext = () => {
    if (!total) return;
    setStartIndex((prevIndex) => (prevIndex + itemsPerPage) % total);
  };

  const handlePrev = () => {
    if (!total) return;
    setStartIndex((prevIndex) => (prevIndex - itemsPerPage + total) % total);
  };

  const visibleDoctors = useMemo(() => {
    if (!total) return [];

    const end = startIndex + itemsPerPage;

    if (end <= total) return resolvedCards.slice(startIndex, end);

    return [...resolvedCards.slice(startIndex)];
  }, [resolvedCards, startIndex, itemsPerPage, total]);

  if (loadingDoctors) {
    return (
      <Box sx={design.LoadingState}>
        <CircularProgress size={28} />
        <span>Cargando doctores...</span>
      </Box>
    );
  }

  return (
    <>
      <Box style={design.Align}>
        {!isMobile && (
          <IconButton onClick={handlePrev}>
            <ArrowBack
              sx={{
                fontSize: 30,
                color: colorIcon ? colorIcon : "#FFF",
              }}
            />
          </IconButton>
        )}
        {visibleDoctors.map((doc, index) => (
          <Link
            component="button"
            underline="none"
            key={doc.uid}
            style={design.Card}
            role="button"
            tabIndex={0}
            onClick={() => handleDoctorDetails(doc)}
          >
            <Box sx={design.BoxAvatar}>
              <Avatar src={doc.imagen} alt={doc.nombre} sx={design.Avatar} />
            </Box>
            <CustomText
              variant="subtitle1"
              color="#333333"
              text={doc.nombre}
              sx={design.TitleCard}
            >
              {doc.nombre}
            </CustomText>
            <CustomText
              variant="subtitle1"
              color="#333333"
              text={doc.especialidad}
              sx={design.Description}
            >
              {doc.especialidad}
            </CustomText>
            <Grid container sx={design.AlignPrice}>
              <Grid size={design.Grid5} alignSelf="center">
                <CustomText
                  variant="subtitle1"
                  color="#2349D0"
                  text="Más información"
                  sx={design.Mas}
                >
                  Más información
                </CustomText>
              </Grid>
            </Grid>
          </Link>
        ))}
        {!isMobile && (
          <IconButton onClick={handleNext}>
            <ArrowForward
              sx={{
                fontSize: 30,
                color: colorIcon ? colorIcon : "#FFF",
              }}
            />
          </IconButton>
        )}
      </Box>
      {isMobile && (
        <Grid container pt={3}>
          <IconButton onClick={handlePrev}>
            <ArrowBack
              sx={{
                fontSize: 30,
                color: colorIcon ? colorIcon : "#FFF",
              }}
            />
          </IconButton>
          <IconButton onClick={handleNext}>
            <ArrowForward
              sx={{
                fontSize: 30,
                color: colorIcon ? colorIcon : "#FFF",
              }}
            />
          </IconButton>
        </Grid>
      )}
      {!directorio && !loadingDoctors && resolvedCards.length === 0 && (
        <CustomText variant="subtitle1" color="#666" sx={design.EmptyState}>
          No se encontraron médicos con esos filtros.
        </CustomText>
      )}
      {!directorio && (
        <CustomText
          variant="subtitle1"
          color="#2349D0"
          sx={{
            ...design.medicos,
            cursor: "pointer",
          }}
        >
          Ir al directorio de médicos <KeyboardArrowRight />
        </CustomText>
      )}
    </>
  );
};

export default CarouselDoctors;
const design = {
  BoxAvatar: {
    border: "1px solid #2349D0",
    borderRadius: "50%",
  },
  Avatar: {
    width: 120,
    height: 120,
    bgcolor: "#fff",
  },
  medicos: {
    fontFamily: "Plus Jakarta Sans",
    fontSize: 17,
    fontWeight: 600,
    lineHeight: "normal",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    marginTop: 3,
  },
  EmptyState: {
    fontFamily: "Plus Jakarta Sans",
    fontSize: 15,
    marginTop: 2,
    textAlign: "center",
    width: "100%",
  },
  TitleCard: {
    fontFamily: "Plus Jakarta Sans",
    fontSize: 25,
    fontWeight: 700,
    lineHeight: "normal",
    marginTop: 1,
  },
  Description: {
    fontFamily: "Plus Jakarta Sans",
    fontSize: 15,
    lineHeight: "normal",
    textAlign: "left",
    marginTop: 1,
    marginLeft: 2,
  },
  Align: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    marginTop: "2%",
  },
  Card: {
    width: "350px",
    height: "330px",
    background: "#F1F1F1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    borderRadius: 15,
    boxShadow: "0 0 5px rgba(0,0,0,0.1)",
  },
  AlignPrice: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
    marginBottom: 1,
    paddingLeft: 1,
    paddingRight: 1,
  },
  Mas: {
    fontFamily: "Plus Jakarta Sans",
    fontSize: 15,
    lineHeight: "normal",
    textAlign: "left",
  },
  LoadingState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    marginTop: 3,
    color: "#666",
    fontFamily: "Plus Jakarta Sans",
  },
  Grid5: { xs: 12, md: 5, xl: 5, lg: 5 },
};
